import { useState, useCallback } from 'react';
import { useTopology, TopologyNode } from '../hooks/useHttpPolling';
import TopologyGraph from '../components/TopologyGraph';
import { 
  restartNode, 
  getNodeSshCommand, 
  LatticeActionResult 
} from '../services/LatticeService';
import './Topology.css';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface ConfirmDialog {
  action: 'restart';
  nodeId: string;
  nodeName: string;
}

type ViewMode = 'graph' | 'table';

export default function Topology() {
  const { data: topology, loading, error } = useTopology();
  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const [selectedNode, setSelectedNode] = useState<TopologyNode | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  // Show notification
  const showNotification = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    const id = `${Date.now()}-${Math.random()}`;
    setNotifications(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  // Handle View Logs - opens GCP Cloud Logging
  const handleViewLogs = useCallback((node: TopologyNode) => {
    const logsUrl = `https://console.cloud.google.com/logs/query;query=resource.labels.node_id%3D"${node.id}"`;
    window.open(logsUrl, '_blank');
    showNotification('info', `Opened logs for node ${node.id.slice(0, 16)}...`);
  }, [showNotification]);

  // Handle SSH Command - copy to clipboard
  const handleCopySshCommand = useCallback(async (node: TopologyNode) => {
    const command = getNodeSshCommand(node.id, node.publicIp ?? undefined);
    try {
      await navigator.clipboard.writeText(command);
      showNotification('success', 'SSH command copied to clipboard');
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = command;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showNotification('success', 'SSH command copied to clipboard');
    }
  }, [showNotification]);

  // Handle Restart Node
  const handleRestartNode = useCallback(async (nodeId: string, nodeName: string) => {
    setActionLoading(prev => ({ ...prev, [nodeId]: true }));
    
    const result: LatticeActionResult = await restartNode(nodeId);
    
    setActionLoading(prev => ({ ...prev, [nodeId]: false }));
    setConfirmDialog(null);
    
    if (result.success) {
      showNotification('success', `Node ${nodeName} restart initiated`);
    } else {
      showNotification('error', result.error || 'Failed to restart node');
    }
  }, [showNotification]);
  
  if (loading) {
    return (
      <div className="topology-page animate-in">
        <div className="loading-spinner">Loading topology...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="topology-page animate-in">
        <div className="error-message">Error: {error.message}</div>
      </div>
    );
  }
  
  if (!topology) {
    return (
      <div className="topology-page animate-in">
        <div className="empty-state">No topology data available</div>
      </div>
    );
  }
  
  return (
    <div className="topology-page animate-in">
      {/* Notifications */}
      <div className="notifications">
        {notifications.map(n => (
          <div key={n.id} className={`notification notification-${n.type}`}>
            <span>{n.message}</span>
            <button onClick={() => setNotifications(prev => prev.filter(x => x.id !== n.id))}>×</button>
          </div>
        ))}
      </div>
      
      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="modal-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Confirm {confirmDialog.action}</h3>
            <p>
              Are you sure you want to {confirmDialog.action} node "{confirmDialog.nodeName}"?
            </p>
            <div className="dialog-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setConfirmDialog(null)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-warning"
                onClick={() => handleRestartNode(confirmDialog.nodeId, confirmDialog.nodeName)}
                disabled={actionLoading[confirmDialog.nodeId]}
              >
                {actionLoading[confirmDialog.nodeId] ? 'Restarting...' : 'Restart'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header with View Toggle */}
      <div className="topology-header">
        <div className="topology-stats">
          <StatCard 
            label="Total Nodes" 
            value={topology.nodes.length} 
          />
          <StatCard 
            label="Healthy" 
            value={topology.nodes.filter(n => n.status === 'healthy').length}
            status="success"
          />
          <StatCard 
            label="Degraded" 
            value={topology.nodes.filter(n => n.status === 'degraded').length}
            status="warning"
          />
          <StatCard 
            label="Offline" 
            value={topology.nodes.filter(n => n.status === 'offline' || n.status === 'error').length}
            status="error"
          />
          <StatCard 
            label="Links" 
            value={topology.links.length}
          />
        </div>
        
        {/* View Toggle */}
        <div className="view-toggle">
          <button 
            className={`view-toggle-btn ${viewMode === 'graph' ? 'active' : ''}`}
            onClick={() => setViewMode('graph')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="5" cy="6" r="3"/>
              <circle cx="19" cy="6" r="3"/>
              <circle cx="12" cy="18" r="3"/>
              <line x1="7.5" y1="7.5" x2="10.5" y2="16"/>
              <line x1="16.5" y1="7.5" x2="13.5" y2="16"/>
            </svg>
            Graph
          </button>
          <button 
            className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="3" y1="15" x2="21" y2="15"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
            Table
          </button>
        </div>
      </div>
      
      {/* Graph View */}
      {viewMode === 'graph' && (
        <TopologyGraph 
          topology={topology}
          height={500}
          onNodeClick={setSelectedNode}
        />
      )}
      
      {/* Table View */}
      {viewMode === 'table' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Network Nodes</h2>
            <span className="version-badge">v{topology.version}</span>
          </div>
          
          <table className="table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Node ID</th>
                <th>Type</th>
                <th>Provider</th>
                <th>Region</th>
                <th>Version</th>
                <th>Uptime</th>
              </tr>
            </thead>
            <tbody>
              {topology.nodes.map((node) => (
                <NodeRow 
                  key={node.id} 
                  node={node} 
                  selected={selectedNode?.id === node.id}
                  onClick={() => setSelectedNode(node)}
                />
              ))}
              {topology.nodes.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-state">
                    No nodes connected to the network
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Node Detail Panel */}
      {selectedNode && (
        <NodeDetailPanel 
          node={selectedNode} 
          onClose={() => setSelectedNode(null)}
          onViewLogs={() => handleViewLogs(selectedNode)}
          onCopySsh={() => handleCopySshCommand(selectedNode)}
          onRestart={() => setConfirmDialog({
            action: 'restart',
            nodeId: selectedNode.id,
            nodeName: `${selectedNode.type} (${selectedNode.region})`,
          })}
          actionLoading={actionLoading[selectedNode.id]}
        />
      )}
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  status 
}: { 
  label: string; 
  value: number;
  status?: 'success' | 'warning' | 'error';
}) {
  return (
    <div className={`stat-card ${status || ''}`}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

interface NodeRowProps {
  node: TopologyNode;
  selected?: boolean;
  onClick?: () => void;
}

function NodeRow({ node, selected, onClick }: NodeRowProps) {
  const statusClass = node.status;
  const uptime = node.uptimeSeconds 
    ? formatUptime(Number(node.uptimeSeconds))
    : 'N/A';
  
  return (
    <tr 
      className={`node-row ${selected ? 'selected' : ''}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <td>
        <span className={`status-dot ${statusClass}`} />
      </td>
      <td>
        <code className="node-id">{node.id.slice(0, 16)}...</code>
      </td>
      <td>
        <span className={`badge badge-${getTypeColor(node.type)}`}>{node.type}</span>
      </td>
      <td>{node.cloud}</td>
      <td>{node.region}</td>
      <td>
        <code>{node.version || 'unknown'}</code>
      </td>
      <td>{uptime}</td>
    </tr>
  );
}

function getTypeColor(type: string): string {
  const colors: Record<string, string> = {
    edge: 'info',
    scatter: 'purple',
    consensus: 'warning',
    ops: 'success',
    bridge: 'danger',
    cloudflare: 'orange',
  };
  return colors[type] || 'secondary';
}

// Node detail panel for expanded view
interface NodeDetailPanelProps {
  node: TopologyNode;
  onClose: () => void;
  onViewLogs: () => void;
  onCopySsh: () => void;
  onRestart: () => void;
  actionLoading?: boolean;
}

function NodeDetailPanel({ 
  node, 
  onClose, 
  onViewLogs, 
  onCopySsh, 
  onRestart,
  actionLoading 
}: NodeDetailPanelProps) {
  const uptime = node.uptimeSeconds 
    ? formatUptime(Number(node.uptimeSeconds))
    : 'N/A';
  
  return (
    <div className="node-detail-panel card">
      <div className="card-header">
        <h3 className="card-title">
          <span className={`badge badge-${getTypeColor(node.type)}`}>{node.type}</span>
          Node Details
        </h3>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
      </div>
      
      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">Node ID</span>
          <code className="detail-value">{node.id}</code>
        </div>
        <div className="detail-item">
          <span className="detail-label">Status</span>
          <span className={`status-badge status-${node.status}`}>{node.status}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Cloud</span>
          <span className="detail-value">{node.cloud}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Region</span>
          <span className="detail-value">{node.region}</span>
        </div>
        {node.zone && (
          <div className="detail-item">
            <span className="detail-label">Zone</span>
            <span className="detail-value">{node.zone}</span>
          </div>
        )}
        <div className="detail-item">
          <span className="detail-label">Version</span>
          <code className="detail-value">{node.version || 'unknown'}</code>
        </div>
        <div className="detail-item">
          <span className="detail-label">Uptime</span>
          <span className="detail-value">{uptime}</span>
        </div>
        {node.publicIp && (
          <div className="detail-item">
            <span className="detail-label">Public IP</span>
            <code className="detail-value">{node.publicIp}</code>
          </div>
        )}
        {node.privateIp && (
          <div className="detail-item">
            <span className="detail-label">Private IP</span>
            <code className="detail-value">{node.privateIp}</code>
          </div>
        )}
      </div>
      
      {node.metrics && (
        <div className="metrics-section">
          <h4>Metrics</h4>
          <div className="metrics-grid">
            <div className="metric-item">
              <span className="metric-value">{node.metrics.cpuPercent?.toFixed(1)}%</span>
              <span className="metric-label">CPU</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{node.metrics.memoryPercent?.toFixed(1)}%</span>
              <span className="metric-label">Memory</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{node.metrics.connections}</span>
              <span className="metric-label">Connections</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{node.metrics.messagesPerSecond}</span>
              <span className="metric-label">Msg/sec</span>
            </div>
          </div>
        </div>
      )}
      
      <div className="node-actions">
        <button className="btn btn-secondary btn-sm" onClick={onViewLogs}>
          View Logs
        </button>
        <button className="btn btn-secondary btn-sm" onClick={onCopySsh}>
          Copy SSH Command
        </button>
        <button 
          className="btn btn-warning btn-sm" 
          onClick={onRestart}
          disabled={actionLoading}
        >
          {actionLoading ? 'Restarting...' : 'Restart'}
        </button>
      </div>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
